import { api } from "@page/api/client";
import { validateSearch } from "@page/api/utils";
import { AppPendingSkeleton } from "@page/components/app-pending-skeleton";
import { MailAttachments } from "@page/components/mail-attachments";
import { MailBodyFrame } from "@page/components/mail-body-frame";
import { MailFab } from "@page/components/mail-fab";
import { MailMeta } from "@page/components/mail-meta";
import { MailStatusBadges } from "@page/components/mail-status-badges";
import { useBackButton } from "@page/hooks/use-back-button";
import {
  buildMailAttachmentUrl,
  mailContentQueryOptions,
} from "@page/utils/mail-content";
import { openExternalLink } from "@page/utils/tg";
import { Type as t } from "@sinclair/typebox";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";

const MailPreviewPage = () => {
  const { id: emailMessageId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const onSetReminder = useCallback(() => {
    const back = window.location.pathname + window.location.search;
    navigate({
      to: "/telegram-app/reminders",
      search: {
        accountId: search.accountId,
        emailMessageId,
        token: search.t,
        back,
      },
    });
  }, [navigate, search.accountId, search.t, emailMessageId]);
  // CORS 代理 toggle —— 默认开启，由 MailFab SecondaryButton 切换；
  // MailBodyFrame 根据这个值决定渲染 proxiedHtml 还是 rawHtml。
  const [useProxy, setUseProxy] = useState(true);

  const queryOptions = mailContentQueryOptions({
    emailMessageId,
    accountId: search.accountId,
    token: search.t,
    folder: search.folder,
  });
  const q = useQuery(queryOptions);

  // BackButton：URL 带 ?back= 时显示并跳回该 URL；没有就隐藏（从 bot 按钮直接进来）
  useBackButton(search.back);

  if (q.isLoading) {
    return <AppPendingSkeleton surface="miniapp" />;
  }
  if (q.isError || !q.data) {
    return <div className="p-5 text-sm text-red-400">邮件加载失败</div>;
  }

  const d = q.data;

  return (
    <>
      {/* 结构和间距都对齐 web `/mail/:id` —— 正常内容流（不置顶），滚动时
         subject/meta 跟着走。MailFab 底部 TG 原生按钮代替 web 的 toolbar。 */}
      <article className="max-w-3xl mx-auto px-4 py-6 wrap-break-word">
        {d.meta.subject && (
          <Subject subject={d.meta.subject} webMailUrl={d.webMailUrl} />
        )}

        <MailStatusBadges inArchive={d.inArchive} starred={d.starred} />

        <MailMeta meta={d.meta} accountEmail={d.accountEmail} />

        <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden shadow-[inset_0_8px_16px_-12px_rgba(0,0,0,0.5)]">
          <MailBodyFrame
            bodyHtml={d.bodyHtml}
            bodyHtmlRaw={d.bodyHtmlRaw}
            useProxy={useProxy}
          />
        </div>

        <MailAttachments
          attachments={d.attachments}
          getDownloadUrl={(attachmentId) =>
            buildMailAttachmentUrl({
              emailMessageId,
              accountId: search.accountId,
              token: search.t,
              folder: d.folder,
              attachmentId,
            })
          }
        />
      </article>

      <MailFab
        emailMessageId={emailMessageId}
        accountId={search.accountId}
        token={search.t}
        starred={d.starred}
        inJunk={d.inJunk}
        inArchive={d.inArchive}
        canArchive={d.canArchive}
        folder={search.folder}
        subject={d.meta.subject ?? null}
        webMailUrl={d.webMailUrl}
        tgMessageLink={d.tgMessageLink}
        useProxy={useProxy}
        onRefresh={
          d.tgMessageLink
            ? async () => {
                const { error } = await api.api
                  .mail({ id: emailMessageId })
                  .refresh.post({
                    accountId: search.accountId,
                    token: search.t,
                  });
                if (error) {
                  const value = error.value as { error?: string } | string;
                  throw new Error(
                    typeof value === "string"
                      ? value
                      : (value.error ?? "刷新失败"),
                  );
                }
              }
            : undefined
        }
        onToggleProxy={() => setUseProxy((v) => !v)}
        onSetReminder={onSetReminder}
        onChanged={() =>
          qc.invalidateQueries({ queryKey: queryOptions.queryKey })
        }
      />
    </>
  );
};

/**
 * 邮件标题：结构沿用 web 的 `<h1>` 尺寸（text-2xl → text-[32px]）和布局，
 * 但 miniapp 这里 subject 是个实实在在的 link（点击 → TG 浏览器打开原文）。
 * 为了让用户看出来可点，标题染 emerald 并带下划线，再加个 `↗` 图标；
 * 无 webMailUrl 时退化成 web 同款的纯 zinc-100 h1。
 */
const Subject = ({
  subject,
  webMailUrl,
}: {
  subject: string;
  webMailUrl: string;
}) => {
  const baseClass =
    "text-2xl sm:text-[28px] md:text-[32px] font-semibold tracking-tight leading-tight wrap-break-word mb-4";

  if (!webMailUrl) {
    return <h1 className={`${baseClass} text-zinc-100`}>{subject}</h1>;
  }

  const openInBrowser = (e: React.MouseEvent) => {
    e.preventDefault();
    openExternalLink(webMailUrl);
  };

  return (
    <h1 className={baseClass}>
      <a
        href={webMailUrl}
        onClick={openInBrowser}
        title="在浏览器打开"
        className="text-emerald-400 underline decoration-emerald-500/40 decoration-2 underline-offset-4 active:opacity-60"
      >
        {subject}
        <span className="text-base font-normal opacity-70 ml-2">↗</span>
      </a>
    </h1>
  );
};

const Search = t.Object({
  accountId: t.Number(),
  t: t.String(),
  folder: t.Optional(
    t.Union([t.Literal("inbox"), t.Literal("junk"), t.Literal("archive")]),
  ),
  back: t.Optional(t.String()),
});
const validateMailSearch = validateSearch(Search);

export const Route = createFileRoute("/telegram-app/mail/$id")({
  component: MailPreviewPage,
  validateSearch: validateMailSearch,
});
